import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import axios from "axios";
import Select2Multiple from 'v-select2-multiple-component';
import CONFIG from "@/assets/js/config.custom.js";
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import ValidationService from '@/services/ValidationService';
import store from "@/store";

export default {
    name: 'enrollmentPaymentCreate',
    components: {
        MainLayout,
        Loading,
        Select2Multiple,
        flatPickr,
    },
    mixins: [ValidationService],
    data() {
        return {
            isLoading: false,
            studentInfo: {},
            tuitionPaymentId: null,
            paymentPlan: CONFIG.PAYMENT_PLANS,
            originalTuitionFee: 0,
            proratedTuitionFee: 0,
            finalTuitionFee: 0,
            formattedFinalTuitionFee: 0,
            annualFee: 0,
            termlyFee: 0,
            monthlyFee: 0,
            paymentPlanId: null,
            specialDiscounts: [],
            discountTypes: [],
			discountLabels: [],
            discountLimit: null,
			discountTypeId: null,
			hasDiscountError: false,
            tuitionPaymentPlans: [],
            firstAttendanceDate: new Date().toISOString().slice(0, 10),
            firstCalculationDate: new Date().toISOString().slice(0, 10),
            terms: [],
            config: {
                altFormat: 'd/m/Y',
                dateFormat: "Y-m-d",
                altInput: true,
            },
            disabled: false,
            proratedInfo: {},
            academicYearOptions: [],
            selectedAcdYearId: null,
            classOptionsForEnrollment: [],
            selectedNextYearClassId: null,
            applyingTuitionPaymentRestore: false,
            pendingRestoreClassId: null,
        };
    },

    computed: {
        isEnrollmentCurrentAcademicYear() {
            if (!this.studentInfo.active_acdemic_year_id || this.selectedAcdYearId == null) {
                return false;
            }
            return Number(this.selectedAcdYearId) === Number(this.studentInfo.active_acdemic_year_id);
        },
        enrollmentSubmitBlocked() {
            if (!this.isEnrollmentCurrentAcademicYear && !this.selectedNextYearClassId) {
                return true;
            }
            return this.disabled;
        },
        hasLifetimeDiscount() {
            return this.specialDiscounts.some((item) => !!item.is_lifetime);
        },
        hasMultipleLifetimeDiscounts() {
            const lifetimeIds = this.specialDiscounts
                                    .filter((item) => !!item.is_lifetime && item?.id != null && item?.id !== "")
                                    .map((item) => Number(item.id));
            return new Set(lifetimeIds).size > 1;
        },
        paymentPlanOptions() {
            const base = Array.isArray(CONFIG.PAYMENT_PLANS) ? CONFIG.PAYMENT_PLANS : [];
            const terms = Array.isArray(this.terms) ? this.terms : [];
            if (!this.firstCalculationDate || terms.length === 0) {
                return base.filter((p) =>
                                Number(p?.id) === CONFIG.PAYMENT_PLAN_IDS.YEARLY
                            );
            }

            const calcDate = this.parseYmdToDate(this.firstCalculationDate);
            if (!calcDate) {
                return base;
            }

            const parsedTerms = terms
                .map((t) => {
                    const start = this.parseYmdToDate(t?.start_date);
                    const end = this.parseYmdToDate(t?.end_date);
                    if (!start || !end) {
                        return null;
                    }
                    return { ...t, _start: start, _end: end };
                })
                .filter(Boolean);

            if (parsedTerms.length === 0) {
                return base;
            }

            const lastTerm = parsedTerms.reduce((latest, cur) => {
                if (!latest) {
                    return cur;
                }
                return cur._end.getTime() >= latest._end.getTime() ? cur : latest;
            }, null);

            if (!lastTerm) {
                return base;
            }

            const inLastTerm = calcDate.getTime() >= lastTerm._start.getTime();

            if (!inLastTerm) {
                return base;
            }

            const isFirstMonthOfLastTerm =
                calcDate.getFullYear() === lastTerm._start.getFullYear() &&
                calcDate.getMonth() === lastTerm._start.getMonth();

            // Always hide One-Time in last term (per requirement).
            if (isFirstMonthOfLastTerm) {
                return base.filter((p) =>
                    Number(p?.id) === CONFIG.PAYMENT_PLAN_IDS.TERMLY ||
                    Number(p?.id) === CONFIG.PAYMENT_PLAN_IDS.MONTHLY
                );
            }

            return base.filter((p) => Number(p?.id) === CONFIG.PAYMENT_PLAN_IDS.MONTHLY);
        },
    },

    watch: {
        firstCalculationDate() {
            this.resetTuitionCalculationOnFirstCalculationDateChange();
        },
        paymentPlanId() {
            this.fetchTuitionFees().then(() => this.applyPaymentPlanToOriginalFee());
        },
        paymentPlanOptions() {
            this.ensureSelectedPaymentPlanIsAllowed();
        },
        discountTypeId(value) {
            if (value !== null && value !== undefined && value !== "") {
                this.hasDiscountError = false;
                this.clearValidationError('discount_type_id');
            }
        },
        selectedAcdYearId() {
            this.fetchTermsForSelectedAcademicYear();
            if (this.applyingTuitionPaymentRestore) return;
            this.syncEnrollmentAcademicYearState();
            this.resetTuitionCalculationOnFirstCalculationDateChange();
            this.refreshDiscountsForSelectedAcademicYear();
        },
        selectedNextYearClassId(newVal) {
            if (this.applyingTuitionPaymentRestore) return;
            if (!this.studentInfo.active_acdemic_year_id) return;
            if (this.isEnrollmentCurrentAcademicYear) return;
            if (!newVal || !this.$route.params.classStudentId) return;
            this.fetchTuitionFees().then(() =>
                this.applyPaymentPlanToOriginalFee()
            );
        },
    },

    methods: {
        clearValidationError(fieldName) {
            if (!fieldName || !this.validationErrors || !Object.prototype.hasOwnProperty.call(this.validationErrors, fieldName)) {
                return;
            }

            this.$delete(this.validationErrors, fieldName);
        },

        setValidationError(fieldName, message) {
            if (!fieldName || !message) {
                return;
            }

            this.$set(this.validationErrors, fieldName, [message]);
        },

        getSelectedDiscountCount() {
            return this.specialDiscounts.filter((item) => item?.id !== null && item?.id !== undefined && item?.id !== "").length;
        },

        getAutoAppliedDiscountCount() {
            return this.specialDiscounts.filter((item) => {
                if (item?.id === null || item?.id === undefined || item?.id === "") {
                    return false;
                }
                return !!item.is_auto_applied;
            }).length;
        },

        getManualDiscountCount(discountCount = this.getSelectedDiscountCount()) {
            return Math.max(0, discountCount - this.getAutoAppliedDiscountCount());
        },

        getEffectiveDiscountLimit() {
            if (!Number.isInteger(this.discountLimit) || this.discountLimit < 0) {
                return null;
            }
            return this.getAutoAppliedDiscountCount() + this.discountLimit;
        },

        syncDiscountLimitValidation(discountCount = this.getSelectedDiscountCount()) {
            if (!Number.isInteger(this.discountLimit) || this.discountLimit < 0) {
                this.clearValidationError('discounts');
                return true;
            }

            const manualCount = this.getManualDiscountCount(discountCount);
            if (manualCount > this.discountLimit) {
                const discountLabel = this.discountLimit === 1 ? 'discount' : 'discounts';
                this.setValidationError('discounts', `You can add up to ${this.discountLimit} additional ${discountLabel}.`);
                return false;
            }

            this.clearValidationError('discounts');
            return true;
        },

        resetTuitionCalculationOnFirstCalculationDateChange() {
            if (this.applyingTuitionPaymentRestore) {
                return;
            }
            this.clearValidationErrors();
            this.paymentPlanId = null;
            this.originalTuitionFee = 0;
            this.proratedTuitionFee = 0;
            this.finalTuitionFee = 0;
            this.formattedFinalTuitionFee = 0;
            this.tuitionPaymentPlans = [];
            this.proratedInfo = {};
        },
        parseYmdToDate(dateStr) {
            if (typeof dateStr !== "string") {
                return null;
            }
            const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (!m) {
                return null;
            }
            const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);
            return Number.isNaN(d.getTime()) ? null : d;
        },
        fetchTermsForSelectedAcademicYear() {
            if (this.selectedAcdYearId == null || this.selectedAcdYearId === "") {
                this.terms = [];
                return Promise.resolve();
            }
            const acdYearId = Number(this.selectedAcdYearId);
            if (Number.isNaN(acdYearId)) {
                this.terms = [];
                return Promise.resolve();
            }
            return axios
                .get(`period/${acdYearId}/term/list`)
                .then((res) => {
                    this.terms = Array.isArray(res.data.terms) ? res.data.terms : [];
                    this.ensureSelectedPaymentPlanIsAllowed();
                })
                .catch(() => {
                    this.terms = [];
                });
        },
        ensureSelectedPaymentPlanIsAllowed() {
            const opts = Array.isArray(this.paymentPlanOptions) ? this.paymentPlanOptions : [];
            if (this.paymentPlanId === null || this.paymentPlanId === undefined || this.paymentPlanId === "") {
                return;
            }
            const selected = Number(this.paymentPlanId);
            if (Number.isNaN(selected)) {
                return;
            }
            const allowed = opts.some((o) => Number(o?.id) === selected);
            if (allowed) {
                return;
            }

            // Prefer Monthly if available, else clear selection.
            const monthlyAllowed = opts.some((o) => Number(o?.id) === CONFIG.PAYMENT_PLAN_IDS.MONTHLY);
            this.paymentPlanId = monthlyAllowed ? CONFIG.PAYMENT_PLAN_IDS.MONTHLY : this.paymentPlanId;
        },
        /**
         * Show student list (for breadcrumb)
         */
        showStudentList() {
            this.$router.push({
                path: "/student/list"
            });
        },

        /**
         * Show student profile (for breadcrumb)
         */
        showProfile(studentInfo) {
            let leavedStatus = "active";
            if (!studentInfo.student_id) {
                leavedStatus = "leaved";
            }
            this.$router.push({
                name: "studentProfile",
                params: {
                    leavedStatus: leavedStatus,
                    id: this.$route.params.classStudentId,
                }
            });
        },

        /**
         * Get student info
         */
        getStudentInfo() {
            axios.get(`enrollment/student/info/${this.$route.params.classStudentId}`)
                .then(async response => {
                    this.studentInfo = response.data;
                    this.specialDiscounts = this.applyLifetimeDiscountRules(
                        this.normalizeDiscountRows(response.data.special_discounts || [])
                    );
                    this.syncDiscountLimitValidation();
                    await this.getOldTuitionPlan();
                    if (!Array.isArray(this.academicYearOptions) || this.academicYearOptions.length === 0) {
                        await this.initAcademicYearOptions();
                    }
                })
                .catch(error => {
                    console.log("Error fetching student info:", error);
                    this.isLoading = false;
                    const msg = error.response?.data?.error || "Failed to load student.";
                    this.$notification.error(msg, {
                        timer: 3,
                        position: "bottomCenter",
                    });
                });
        },

        refreshDiscountsForSelectedAcademicYear() {
            if (this.applyingTuitionPaymentRestore) {
                return;
            }
            axios.get(`enrollment/student/info/${this.$route.params.classStudentId}`, {
                params: { acd_year_id: this.selectedAcdYearId }
            })
                .then((response) => {
                    this.specialDiscounts = this.applyLifetimeDiscountRules(
                        this.normalizeDiscountRows(response.data.special_discounts || [])
                    );
                    this.syncDiscountLimitValidation();
                    this.calculateFinalFee();
                })
                .catch((error) => {
                    console.log("Error refreshing discounts:", error);
                });
        },

        /**
         * Get old tuition plan values from db 
         * In the case of user get back from invoice create page
         */
        getOldTuitionPlan() {
            const rawId = this.tuitionPaymentId;
            const numId = Number(rawId);
            if (!rawId || Number.isNaN(numId)) {
                return;
            }
            return axios.get(`enrollment/student/tuition-plan/${rawId}`)
                .then(async response => {
                    const tuitionPayment = response.data;
                    if (
                        !tuitionPayment ||
                        (Array.isArray(tuitionPayment.tuition_payment) &&
                            tuitionPayment.tuition_payment.length === 0)
                    ) {
                        return;
                    }
                    this.applyingTuitionPaymentRestore = true;
                    this.firstAttendanceDate = tuitionPayment.first_attendance_date;
                    this.firstCalculationDate = tuitionPayment.first_calculation_date;
                    this.originalTuitionFee = tuitionPayment.original_tuition_fee;
                    this.proratedTuitionFee = tuitionPayment.prorated_tuition_fee;
                    this.finalTuitionFee = tuitionPayment.final_tuition_fee;
                    this.specialDiscounts = this.applyLifetimeDiscountRules(
                        this.normalizeDiscountRows(tuitionPayment.tuition_discounts || [])
                    );
                    this.syncDiscountLimitValidation();
                    this.tuitionPaymentPlans = tuitionPayment.payment_plans || [];
                    this.paymentPlanId = tuitionPayment.payment_plan_id
                    this.formattedFinalTuitionFee = tuitionPayment.formatted_final_tuition_fee || 0;
                    const parentId = tuitionPayment?.acd_year_id != null ? Number(tuitionPayment.acd_year_id) : null;
                    const currentId = this.studentInfo?.active_acdemic_year_id != null ? Number(this.studentInfo.active_acdemic_year_id) : null;
                    const shouldForceShowCurrentYear =
                        parentId != null &&
                        currentId != null &&
                        parentId === currentId &&
                        this.studentInfo?.has_tuition_for_current_acd_year;
                    if (shouldForceShowCurrentYear) {
                        await this.initAcademicYearOptions({ forceShowCurrentYear: true });
                    } else {
                        await this.initAcademicYearOptions();
                        if (parentId) {
                            this.selectedAcdYearId = parentId;
                            await this.loadClassListForTerm(parentId);
                            this.selectedNextYearClassId = tuitionPayment.class_id;
                        }
                    }
                    this.calculateProratedTuitionFee();
                    this.$nextTick(() => {
                        this.applyingTuitionPaymentRestore = false;
                    });
                })
                .catch(error => {
                    this.applyingTuitionPaymentRestore = false;
                    console.log("Error fetching tuition plan:", error);
                });
        },

        /**
         * Build Academic Year options: if hideCurrentYear, drop the current parent year and keep all others;
         * otherwise keep the full list. Default selection is current year, or the first remaining year when current is hidden.
         *
         * @param {Array<{id:number,text:string}>} years Raw rows from getAcdYearsAfterOrEqualDate (+ current unshifted upstream).
         * @param {number} currentId Parent academic year id for the student's current class.
         * @param {boolean} hideCurrentYear When true, omit the current year from options.
         * @returns {{ options: Array<{id:number,text:string}>, selectedId: number|null }}
         */
        buildAcademicYearOptions(years, currentId, hideCurrentYear) {
            const normalized = Array.isArray(years) ? years.slice() : [];
            let selectedId = currentId;
            if (hideCurrentYear) {
                const others = normalized.filter((y) => Number(y.id) !== currentId);
                selectedId = null;
                return { options: others, selectedId };
            }

            return { options: normalized, selectedId: selectedId };
        },

        /**
         * Load academic year dropdown and default selection (Phase 1: form only).
         */
        initAcademicYearOptions({ forceShowCurrentYear = false } = {}) {
            const currentId = Number(this.studentInfo.active_acdemic_year_id);
            const endDate = this.studentInfo.acd_year_end_date || new Date().toISOString().slice(0, 10);
            const hideCurrentYear = this.studentInfo.has_tuition_for_current_acd_year && !forceShowCurrentYear;

            return axios.get("period/getAcdYearsAfterOrEqualDate", { params: { end_date: endDate } })
                .then((res) => {
                    const years = Array.isArray(res.data) ? res.data.slice() : [];
                    const { options, selectedId } = this.buildAcademicYearOptions(
                        years,
                        currentId,
                        hideCurrentYear
                    );
                    this.academicYearOptions = options;
                    this.selectedAcdYearId = selectedId;
                    if (hideCurrentYear && options.length === 0) {
                        this.$notification.error(
                            "No other academic year is available. Please create new academic year data first.",
                            { timer: 5, position: "bottomCenter" }
                        );
                    }
                })
                .catch(() => {
                    const { options, selectedId } = this.buildAcademicYearOptions(
                        [],
                        currentId,
                        hideCurrentYear
                    );
                    if (options.length === 0 && !hideCurrentYear) {
                        this.academicYearOptions = [
                            {
                                id: currentId,
                                text:
                                    this.studentInfo.current_academic_period ||
                                    `Academic year #${currentId}`,
                            },
                        ];
                        this.selectedAcdYearId = currentId;
                    } else {
                        this.academicYearOptions = options;
                        this.selectedAcdYearId = selectedId;
                        if (hideCurrentYear && options.length === 0) {
                            this.$notification.error(
                                "No other academic year is available. Please create new academic year data first.",
                                { timer: 5, position: "bottomCenter" }
                            );
                        }
                    }
                }).finally(() => {
                    this.isLoading = false;
                });
        },

        syncEnrollmentAcademicYearState() {
            if (this.applyingTuitionPaymentRestore) {
                return;
            }
            if (!this.studentInfo.active_acdemic_year_id || this.selectedAcdYearId == null) {
                return;
            }

            const currentId = Number(this.studentInfo.active_acdemic_year_id);
            const selected = Number(this.selectedAcdYearId);

            if (currentId === selected) {
                this.classOptionsForEnrollment = [];
                this.selectedNextYearClassId = null;
                return this.fetchTuitionFees().then(() => this.applyPaymentPlanToOriginalFee());
            }

            this.classOptionsForEnrollment = [];
            this.selectedNextYearClassId = null;
            this.loadClassListForTerm(selected).catch(() => {
                this.classOptionsForEnrollment = [];
                this.selectedNextYearClassId = null;
                this.annualFee = 0;
                this.termlyFee = 0;
                this.monthlyFee = 0;
                this.applyPaymentPlanToOriginalFee();
            });
        },

        /**
         * Class rows for Select2 (string ids — matches v-select2 value/options).
         */
        mapClassRowsForSelect2(raw) {
            const list = Array.isArray(raw) ? raw : [];
            return list.map((row) => ({
                id: row.id != null && row.id !== "" ? String(row.id) : "",
                text: row.text || row.class_name || "",
            }));
        },

        /**
         * Classes for a term (child period id), same as student profile pass/fail flow.
         */
        loadClassListForTerm(academicYearId) {
            const campusId = store.state.user.campus_id;
            return axios
                .get(`class/classList/${academicYearId}`, { params: { campus_id: campusId } })
                .then((res) => {
                    this.classOptionsForEnrollment = this.mapClassRowsForSelect2(res.data);

                    const pending = this.pendingRestoreClassId;
                    this.pendingRestoreClassId = null;

                    const matched = this.classOptionsForEnrollment.find(
                        (c) => pending != null && pending !== "" && String(c.id) === String(pending)
                    );

                    if (matched) {
                        this.selectedNextYearClassId = matched.id;
                    } else if (this.classOptionsForEnrollment.length === 1) {
                        this.selectedNextYearClassId = this.classOptionsForEnrollment[0].id;
                    } else {
                        this.selectedNextYearClassId = null;
                        this.annualFee = 0;
                        this.termlyFee = 0;
                        this.monthlyFee = 0;
                        this.applyPaymentPlanToOriginalFee();
                    }
                })
                .catch(() => {
                    this.classOptionsForEnrollment = [];
                    this.selectedNextYearClassId = null;
                    this.pendingRestoreClassId = null;
                    return Promise.reject(new Error("class list load failed"));
                });
        },

        /**
         * Get student tuition fees
         */
        fetchTuitionFees() {
            if (this.applyingTuitionPaymentRestore) {
                return Promise.resolve();
            }
            const params = {};
            if (this.studentInfo.active_acdemic_year_id && this.selectedAcdYearId != null) {
                const isCurrent =
                    Number(this.selectedAcdYearId) ===
                    Number(this.studentInfo.active_acdemic_year_id);
                if (isCurrent) {
                    params.class_id = this.studentInfo.active_class_id;
                } else if (this.selectedNextYearClassId) {
                    params.class_id = Number(this.selectedNextYearClassId);
                }
            }
            this.isLoading = true;
            return axios
                .get(`enrollment/student/tuition_fees`, { params })
                .then((response) => {
                    this.annualFee = response.data.tuition_fees?.annual_fee || 0;
                    this.termlyFee = response.data.tuition_fees?.termly_fee || 0;
                    this.monthlyFee = response.data.tuition_fees?.monthly_fee || 0;
                    this.isLoading = false;
                })
                .catch((error) => {
                    console.log("Error fetching tuition fees:", error);
                    if (error.response?.status == 404) {
                        this.$notification.error(error.response?.data.error, {
                            timer: 3,
                            position: "bottomCenter",
                        });
                    }
                    this.isLoading = false;
                })
        },

        /**
         * Normalize API discount rows and guarantee a stable unique key per row.
         * @param {Array} discounts
         * @returns {Array}
         */
        normalizeDiscountRows(discounts = []) {
            return discounts.map((discount, index) => {
                const isReferral = !!discount.is_referral_discount;
                const isLifetime = !!discount.is_lifetime;
                const isAutoApplied = discount.is_auto_applied !== undefined && discount.is_auto_applied !== null
                                        ? !!discount.is_auto_applied
                                        : (isReferral || isLifetime || !!discount.is_special_discount);

                return {
                    ...discount,
                    row_key: discount.row_key || `discount-${discount.id}-${index}`,
                    is_referral_discount: isReferral,
                    is_lifetime: isLifetime,
                    is_auto_applied: isAutoApplied,
                };
            });
        },

        /**
         * When lifetime discounts exist, keep lifetime + referral rows and drop other specials.
         * Multiple lifetimes are kept for display; submit validates uniqueness.
         * @param {Array} rows
         * @returns {Array}
         */
        applyLifetimeDiscountRules(rows = []) {
            const hasLifetime = rows.some((item) => !!item.is_lifetime);
            if (!hasLifetime) {
                return rows;
            }
            return rows.filter((item) => !!item.is_lifetime || !!item.is_referral_discount);
        },

        /**
         * Create a unique key for newly added discount rows.
         * @param {number} discountId
         * @returns {string}
         */
        buildDiscountRowKey(discountId) {
            return `manual-${discountId}`;
        },

        /**
         * Calculate discount amount based on discount_type
         * @param {object} discount 
         * @returns 
         */
        calculateDiscountAmount(discount) {
            if (discount.discount_type === CONFIG.DISCOUNT_TYPE.PERCENTAGE) {
				return Math.round((discount.discount_amount / 100) * this.proratedTuitionFee);
            } else if (discount.discount_type === CONFIG.DISCOUNT_TYPE.FIXED) {
                return discount.discount_amount;
            }
            return 0;
        },

        /**
         * Format amount with comma separation and no decimals.
         * @param {Number|String} value
         * @returns {String}
         */
        formatAmount(value) {
            if (value === null || value === undefined) return '0';
            const num = Number(String(value).replace(/,/g, ''));
            if (Number.isNaN(num)) return value;
            return Math.round(num).toLocaleString('en-US');
        },

        /**
         * Set original tuition from selected payment plan and recalculate proration.
         * Uses this.paymentPlanId when planId argument is omitted (e.g. watcher).
         */
        applyPaymentPlanToOriginalFee(planId) {
            if (this.applyingTuitionPaymentRestore) {
                return;
            }
            this.clearValidationErrors();
            const raw =
                planId !== undefined && planId !== null
                    ? planId
                    : this.paymentPlanId;
            if (raw === null || raw === undefined || raw === "") {
                return;
            }
            const planIdNum = Number(raw);
            if (Number.isNaN(planIdNum)) {
                return;
            }

            const isPlanAllowed = Array.isArray(this.paymentPlanOptions) && 
                this.paymentPlanOptions.some(opt => Number(opt?.id) === planIdNum);

            if (!isPlanAllowed) {
                this.calculateProratedTuitionFee();
                return;
            }

            if (planIdNum === CONFIG.PAYMENT_PLAN_IDS.YEARLY) {
                this.originalTuitionFee = this.annualFee;
            } else if (planIdNum === CONFIG.PAYMENT_PLAN_IDS.TERMLY) {
                this.originalTuitionFee = this.termlyFee;
            } else if (planIdNum === CONFIG.PAYMENT_PLAN_IDS.MONTHLY) {
                this.originalTuitionFee = this.monthlyFee;
            }
            this.calculateProratedTuitionFee();
        },

        /**
         * calculate prorated tuition fee
         * @returns 
         */
        calculateProratedTuitionFee() {
            if (!this.firstCalculationDate || this.selectedAcdYearId == null) {
                return;
            }
            const isCurrentYear =
                Number(this.selectedAcdYearId) ===
                Number(this.studentInfo.active_acdemic_year_id);
            if (
                isCurrentYear &&
                (!this.studentInfo.acd_year_start_date || !this.studentInfo.acd_year_end_date)
            ) {
                return;
            }
            axios.post(`enrollment/calculate-prorated-amount`, {
                acd_year_id: this.selectedAcdYearId,
                first_calculation_date: this.firstCalculationDate,
                original_tuition_fee: this.originalTuitionFee,
            })
            .then(response => {
                if (response.data.no_valid_terms) {
                    this.isLoading = false;
                    this.$notification.error(
                        "There is no valid terms for selected academic year. Create term data first.",
                        { timer: 3, position: "bottomCenter" }
                    );
                }
                this.proratedTuitionFee = response.data.prorated_tuition_fee;
                this.proratedInfo = response.data.prorated_info;
                this.calculateFinalFee();
            })
            .catch(error => {
                this.printValidationError(error.response);
                console.log("Error fetching payment plan:", error);
                this.isLoading = false;
            });
        },

        /**
         * Get discount types
         */
        getDiscountTypes() {
			axios.get(`enrollment/discounts`)
                .then(response => {
                   this.discountTypes = response.data.discount_types;
                   this.discountLabels = response.data.discount_labels;
                   const parsedDiscountLimit = Number(response.data.discount_limit);
                   this.discountLimit = Number.isInteger(parsedDiscountLimit) ? parsedDiscountLimit : null;
                   this.syncDiscountLimitValidation();
                })
                .catch(error => {
                    console.log("Error fetching discount types:", error);
                    this.isLoading = false;
                })
        },

        /**
         * Add discount
         * @param {integer} discountTypeId 
         * @returns 
         */
		addDiscount(discountTypeId) {
            this.clearValidationError('discount_type_id');

            if (this.hasLifetimeDiscount) {
                return;
            }

			if (discountTypeId == null || discountTypeId === "") {
				this.hasDiscountError = true;
				return;
			}
            this.hasDiscountError = false;
			const discount = this.discountTypes.find(item => item.id === Number(discountTypeId));
            
            if (discount) {
                const isDuplicate = this.specialDiscounts.some(item => item.id === discount.id);
                if(!isDuplicate) {
                    const nextDiscountCount = this.getSelectedDiscountCount() + 1;
                    const nextManualCount = this.getManualDiscountCount(nextDiscountCount);
                    if (Number.isInteger(this.discountLimit) && this.discountLimit >= 0 && nextManualCount > this.discountLimit) {
                        this.syncDiscountLimitValidation(nextDiscountCount);
                        return;
                    }

                    this.specialDiscounts.push({
                        ...discount,
                        row_key: this.buildDiscountRowKey(discount.id),
                        is_referral_discount: false,
                        is_lifetime: false,
                        is_auto_applied: false,
                    });
                    this.syncDiscountLimitValidation();
                    this.calculateFinalFee();
                } else {
                    this.printValidationError({
                        status: 422,
                        data: {
                            errors: {
                                discount_type_id: ["Discount type already added."]
                            }
                        }
                    });
                }
            }
		},

        /**
         * Calculate final tuition fee
         */
        calculateFinalFee() {
            let totalDiscount = 0;
            this.specialDiscounts.forEach(discount => {
                totalDiscount += this.calculateDiscountAmount(discount);
            });
            this.finalTuitionFee = this.proratedTuitionFee - totalDiscount;
            if (this.finalTuitionFee < 0) this.finalTuitionFee = 0;

            this.generatePaymentPlan();
        },

        /**
         * Remove discount
         * @param {string} rowKey 
         */
        removeDiscount(rowKey) {
            this.clearValidationError('discount_type_id');
            this.specialDiscounts = this.specialDiscounts.filter(item => item.row_key !== rowKey);
            this.syncDiscountLimitValidation();
            this.calculateFinalFee();
        },

        /**
         * Generate payment plan
         */
        generatePaymentPlan()
        {
            if (this.paymentPlanId === null || this.paymentPlanId === undefined || this.paymentPlanId === "") {
                return;
            }
            axios.post(`enrollment/generate-payment-plan`, {
                payment_plan_id: this.paymentPlanId,
                final_tuition_fee: this.finalTuitionFee,
                acd_year_id: this.selectedAcdYearId,
                first_calculation_date: this.firstCalculationDate,
                prorated_info: this.proratedInfo,
            })
            .then(response => {
                this.clearValidationErrors();
                this.tuitionPaymentPlans = response.data.payment_plans;
                this.formattedFinalTuitionFee = response.data.total_amount;
            })
            .catch(error => {
                this.printValidationError(error.response);
                this.tuitionPaymentPlans = [];
                this.formattedFinalTuitionFee = 0;
                console.log("Error fetching payment plan:", error);
                this.isLoading = false;
            });
        },

        /**
         * Submit form
         */
        submitForm() {
            if (this.enrollmentSubmitBlocked) {
                if (!this.isEnrollmentCurrentAcademicYear && !this.selectedNextYearClassId) {
                    this.$notification.error(
                        "Please select a class for the selected academic year.",
                        { timer: 3, position: "bottomCenter" }
                    );
                }
                return;
            }
            if (this.hasMultipleLifetimeDiscounts) {
                this.setValidationError('discounts', 'Only one Lifetime Discount can be used.');
                return;
            }
            if (!this.syncDiscountLimitValidation()) {
                return;
            }
            this.isLoading = true;
            const classId = this.isEnrollmentCurrentAcademicYear
                ? this.studentInfo.active_class_id
                : Number(this.selectedNextYearClassId);
            const data = {
                acd_year_id: this.selectedAcdYearId,
                class_id: classId,
                payment_plan_id: this.paymentPlanId,
                discounts: this.specialDiscounts.map(item => item.id),
                first_calculation_date: this.firstCalculationDate,
                first_attendance_date: this.firstAttendanceDate,
            };

            this.saveOrUpdateTuitionPayment(data);
        },

        /**
         * Save or update tuition payment plan
         * @param {Object} data 
         */
        saveOrUpdateTuitionPayment(data) {
            const isUpdate = !!this.tuitionPaymentId;
            const url = isUpdate 
                ? `enrollment/update/tuition-plan/${this.tuitionPaymentId}/${this.$route.params.classStudentId}`
                : `enrollment/save-draft/${this.$route.params.classStudentId}`;

            axios.post(url, data)
                .then(response => {
                    this.isLoading = false;
                    
                    if (response.status != 200) {
                        this.$notification.error(response.data.message, {
                            timer: 3,
                            position: "bottomCenter",
                        });
                        return;
                    }
                    this.$router.push({
                        name: "enrollmentInvoiceCreate",
                        params: {
                            classStudentId: this.$route.params.classStudentId,
                            tuitionPaymentId: isUpdate ? this.tuitionPaymentId : response.data.tuition_payment_id,
                        }
                    });
                })
                .catch(error => {
                    this.isLoading = false;
                    if (error.response.status == 422) {
                        this.printValidationError(error.response);
                    } else if (error.response.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    } else {
                        this.$notification.error(error.response.data.error, {
                            timer: 3,
                            position: "bottomCenter",
                        });
                    }
                    console.log(`Error ${isUpdate ? 'updating' : 'saving'} draft:`, error);
                });
        },
    },

    async mounted() {
        this.isLoading = true;
        this.tuitionPaymentId = this.$route.query.tuitionPaymentId;
        await this.getStudentInfo();
        this.getDiscountTypes();
    }
};
